import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  ApiError,
  compareCatalogLots,
  lotKeyOf,
  LotKindSchema,
  type CatalogOrderable,
  type CatalogOrderSaveInput,
  type CatalogOrderSaveResponse,
  type AuctionItem,
  type Bundle,
  type Category,
  type ItemCreateInput,
  type ItemUpdateInput,
  type BundleCreateInput,
  type BundleUpdateInput,
  type ItemImportRow,
  type ReapplyDefaultsResponse,
} from '@neon-one/auctions-types';
import type { SharedKernel } from '@/shell';

import { adminCatalogApi, type BulkImportResponse, type BulkActionResult } from './api';

export type Kernel = SharedKernel;

const LoadState = { idle: 'idle', loading: 'loading', success: 'success', error: 'error' } as const;
type LoadState = typeof LoadState[keyof typeof LoadState];
const LotKind = LotKindSchema.enum;

export type CatalogEntry = CatalogOrderable & (
  | { kind: typeof LotKind.item; item: AuctionItem }
  | { kind: typeof LotKind.bundle; bundle: Bundle }
);

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `cat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Per-row validation detail the backend attaches to a bulk-import 422
// (VALIDATION_FAILED). Not part of the shared ApiErrorBody wire type
// (which only knows about `fieldErrors`/`failures`), so we narrow it
// locally at runtime instead of trusting the static type.
interface BulkImportFailedRow {
  index: number;
  fieldErrors: Record<string, string[]>;
}

function isFieldErrorsShape(value: unknown): value is Record<string, string[]> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(Array.isArray)
  );
}

function isBulkImportFailedRows(value: unknown): value is BulkImportFailedRow[] {
  return (
    Array.isArray(value) &&
    value.every(
      row =>
        typeof row === 'object' &&
        row !== null &&
        typeof (row as { index?: unknown }).index === 'number' &&
        isFieldErrorsShape((row as { fieldErrors?: unknown }).fieldErrors)
    )
  );
}

function describeFailedRow(row: BulkImportFailedRow): string {
  const rowNumber = row.index + 1;
  const fieldSummary = Object.entries(row.fieldErrors)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
    .join('; ');
  return `Row ${rowNumber}: ${fieldSummary}`;
}

/**
 * Outcome of a bundle composition change (GT1-446). `paused` reports what THIS
 * action did: bids were live, so they were voided and the bundle stopped
 * accepting new ones. A bundle whose stack was already voided reports false —
 * there was nothing left to void — even though it is still paused; read
 * `isBundlePaused` for the standing state.
 */
export interface BundleMemberChange {
  ok: boolean;
  paused: boolean;
  voidedBidCount: number;
}

// Pure — no store state — so it's testable directly without a Pinia instance.
export function describeBulkImportFailure(err: unknown): string {
  if (err instanceof ApiError) {
    const failed = (err.body as { failed?: unknown }).failed;
    if (isBulkImportFailedRows(failed) && failed.length > 0) {
      // Import is all-or-nothing (services/auctions-backend itemImport.ts):
      // one bad row rejects the whole batch, so every row — including ones
      // with no error of their own — is still unimported. Say so up front;
      // otherwise a row with no red error reads as if it went through.
      const rowsWord = failed.length === 1 ? 'row' : 'rows';
      return [
        `No items were imported. ${failed.length} ${rowsWord} failed validation. Fix and try again:`,
        ...failed.map(describeFailedRow),
      ].join('\n');
    }
    return err.body.message;
  }
  return "We couldn't import the rows. Try again.";
}

export const useAdminCatalogStore = defineStore('adminCatalog', () => {
  const auctionUuid = ref<string | null>(null);
  const items = ref<AuctionItem[]>([]);
  const itemsState = ref<LoadState>('idle');
  const itemsError = ref<string | null>(null);
  const selectedItemIds = ref<Set<number>>(new Set());
  // Bundles select into their own set. Item ids and bundle ids are separate
  // sequences that collide freely, so one shared set of numbers would let a
  // selected bundle satisfy an items-only eligibility check — and would seed
  // the bundle composer with ids that are not items at all.
  const selectedBundleIds = ref<Set<number>>(new Set());

  const bundles = ref<Bundle[]>([]);
  const bundlesState = ref<LoadState>('idle');
  const orderSaveState = ref<LoadState>(LoadState.idle);

  const submitState = ref<LoadState>('idle');
  const submitError = ref<string | null>(null);
  const fieldErrors = ref<Record<string, string[]>>({});

  const aiState = ref<LoadState>('idle');
  const aiError = ref<string | null>(null);

  const importState = ref<LoadState>('idle');
  const importResult = ref<BulkImportResponse | null>(null);
  const importError = ref<string | null>(null);

  const bidCounts = ref<Record<string, number>>({});
  const bidCountsState = ref<LoadState>('idle');

  const page = ref(0);
  const pageSize = ref(25);
  const totalCount = computed(() => items.value.length);
  const searchQuery = ref('');
  const categoryFilter = ref<number[]>([]);

  const selectionCount = computed(() => selectedItemIds.value.size + selectedBundleIds.value.size);
  const hasActiveFilters = computed(() => searchQuery.value.trim().length > 0 || categoryFilter.value.length > 0);

  // GT1-446: draft content bidders can't see yet, counted in bidder-visible
  // units — a draft bundle is one unit, and member items never count on
  // their own regardless of their status (the catalog hides them too).
  const bundledItemIds = computed(() => {
    const set = new Set<number>();
    for (const b of bundles.value) for (const c of b.components) set.add(c.auctionItemId);
    return set;
  });
  const draftStandaloneItemIds = computed(() =>
    items.value.filter(i => i.status === 'draft' && !bundledItemIds.value.has(i.id)).map(i => i.id)
  );
  const draftBundleIds = computed(() =>
    bundles.value.filter(b => b.status === 'draft').map(b => b.id)
  );
  const draftContentCount = computed(
    () => draftStandaloneItemIds.value.length + draftBundleIds.value.length
  );

  const catalogEntries = computed<CatalogEntry[]>(() => {
    const bundleEntries: CatalogEntry[] = bundles.value.map(bundle => ({
      kind: LotKind.bundle, id: bundle.id, catalogPosition: bundle.catalogPosition ?? null, bundle,
    }));
    const itemEntries: CatalogEntry[] = items.value
      .filter(item => !bundledItemIds.value.has(item.id))
      .map(item => ({ kind: LotKind.item, id: item.id, catalogPosition: item.catalogPosition ?? null, item }));
    return [...bundleEntries, ...itemEntries].sort(compareCatalogLots);
  });
  const catalogUnitCount = computed(() => catalogEntries.value.length);

  let loadItemsRequestId = 0;
  let loadBundlesRequestId = 0;
  let loadBidCountsRequestId = 0;
  let loadCategoriesRequestId = 0;
  let submitRequestId = 0;
  let orderSaveRequestId = 0;

  async function loadItems(uuid: string): Promise<void> {
    const requestId = ++loadItemsRequestId;
    auctionUuid.value = uuid;
    itemsState.value = 'loading';
    itemsError.value = null;
    try {
      const result = await adminCatalogApi.listItems(uuid);
      if (requestId !== loadItemsRequestId) return;
      items.value = result;
      itemsState.value = 'success';
    } catch {
      if (requestId !== loadItemsRequestId) return;
      itemsState.value = 'error';
      itemsError.value = "We couldn't load the catalog.";
    }
  }

  async function loadBidCounts(uuid: string): Promise<void> {
    const requestId = ++loadBidCountsRequestId;
    bidCountsState.value = 'loading';
    try {
      const result = await adminCatalogApi.getBidCounts(uuid);
      if (requestId !== loadBidCountsRequestId) return;
      bidCounts.value = result;
      bidCountsState.value = 'success';
    } catch {
      if (requestId !== loadBidCountsRequestId) return;
      bidCountsState.value = 'error';
    }
  }

  function bidCountFor(itemId: number, bundleId?: number | null): number {
    if (bundleId != null) return bidCounts.value[`bundle:${bundleId}`] ?? 0;
    return bidCounts.value[String(itemId)] ?? 0;
  }

  function setPage(p: number): void {
    page.value = p;
  }

  function setPageSize(size: number): void {
    pageSize.value = size;
    page.value = 0;
  }

  function setSearch(query: string): void {
    searchQuery.value = query;
    page.value = 0;
  }

  function setCategoryFilter(ids: number[]): void {
    categoryFilter.value = ids;
    page.value = 0;
  }

  async function loadBundles(uuid: string): Promise<void> {
    const requestId = ++loadBundlesRequestId;
    bundlesState.value = 'loading';
    try {
      const result = await adminCatalogApi.listBundles(uuid);
      if (requestId !== loadBundlesRequestId) return;
      bundles.value = result;
      bundlesState.value = 'success';
    } catch {
      if (requestId !== loadBundlesRequestId) return;
      bundlesState.value = 'error';
    }
  }

  async function reloadCatalog(): Promise<void> {
    const uuid = auctionUuid.value;
    if (uuid) await Promise.all([loadItems(uuid), loadBundles(uuid)]);
  }

  function applyCatalogPositions(response: CatalogOrderSaveResponse): void {
    const positions = new Map(response.lots.map(lot => [lotKeyOf(lot), lot.catalogPosition]));
    for (const item of items.value) {
      const key = lotKeyOf({ kind: LotKind.item, id: item.id });
      if (positions.has(key)) item.catalogPosition = positions.get(key)!;
    }
    for (const bundle of bundles.value) {
      const key = lotKeyOf({ kind: LotKind.bundle, id: bundle.id });
      if (positions.has(key)) bundle.catalogPosition = positions.get(key)!;
    }
  }

  async function saveCatalogOrder(input: CatalogOrderSaveInput): Promise<CatalogOrderSaveResponse> {
    const uuid = auctionUuid.value;
    if (!uuid) throw new Error('No auction selected.');
    const requestId = ++orderSaveRequestId;
    orderSaveState.value = LoadState.loading;
    try {
      const response = await adminCatalogApi.saveCatalogOrder(uuid, input);
      if (auctionUuid.value === uuid && requestId === orderSaveRequestId) {
        applyCatalogPositions(response);
        orderSaveState.value = LoadState.success;
      }
      return response;
    } catch (error) {
      if (auctionUuid.value === uuid && requestId === orderSaveRequestId) orderSaveState.value = LoadState.error;
      throw error;
    }
  }

  function applyApiError(err: ApiError): void {
    submitState.value = 'error';
    if (err.code === 'VALIDATION_FAILED' && err.fieldErrors) {
      fieldErrors.value = err.fieldErrors;
      submitError.value = 'Some fields need attention.';
      return;
    }
    // GT1-428: backend returns VALIDATION_FAILED with `lockedFields: string[]`
    // (no `fieldErrors`) when the client attempts to write a pricing field on
    // an item that has active bids. Surface a specific, actionable message
    // instead of the generic "We couldn't save the changes." fallback.
    const lockedFields = (err.body as { lockedFields?: unknown }).lockedFields;
    if (err.code === 'VALIDATION_FAILED' && Array.isArray(lockedFields) && lockedFields.length > 0) {
      const labels = (lockedFields as string[]).map(pricingFieldLabel);
      submitError.value = `Can't change ${humanJoin(labels)}. This item has active bids.`;
      return;
    }
    submitError.value = "We couldn't save the changes.";
  }

  // Map wire pricing-field names to human copy for lockedFields error surface.
  function pricingFieldLabel(field: string): string {
    switch (field) {
      case 'fairMarketValueCents': return 'FMV';
      case 'startingBidCents':     return 'starting bid';
      case 'bidIncrementCents':    return 'bid increment';
      case 'reservePriceCents':    return 'reserve price';
      default:                     return field;
    }
  }
  function humanJoin(parts: string[]): string {
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }

  async function saveItem(
    input: ItemCreateInput | ItemUpdateInput,
    itemId: number | null
  ): Promise<AuctionItem | null> {
    if (!auctionUuid.value) return null;
    submitState.value = 'loading';
    submitError.value = null;
    fieldErrors.value = {};
    try {
      const result =
        itemId === null
          ? await adminCatalogApi.createItem(auctionUuid.value, input as ItemCreateInput, newIdempotencyKey())
          : await adminCatalogApi.updateItem(auctionUuid.value, itemId, input as ItemUpdateInput);
      // Adopt the server record; catalogEntries places new lots in the tail.
      const idx = items.value.findIndex(i => i.id === result.id);
      if (idx >= 0) items.value.splice(idx, 1, result);
      else {
        items.value = [result, ...items.value];
      }
      submitState.value = 'success';
      return result;
    } catch (e) {
      if (e instanceof ApiError) applyApiError(e);
      else submitError.value = "We couldn't save the changes.";
      return null;
    }
  }

  async function withdrawItems(itemIds: number[]): Promise<number> {
    if (!auctionUuid.value) return 0;
    let succeeded = 0;
    for (const id of itemIds) {
      try {
        const result = await adminCatalogApi.withdrawItem(auctionUuid.value, id);
        const idx = items.value.findIndex(i => i.id === result.id);
        if (idx >= 0) items.value.splice(idx, 1, result);
        succeeded += 1;
      } catch {
        // skip
      }
    }
    selectedItemIds.value = new Set();
    return succeeded;
  }

  // ── GT1-446 Flow B: live-bundle withdrawal lifecycle ──────────────────
  // Pause and per-member withdrawal are SERVER state (bundles.is_paused,
  // bundle_components.withdrawn_at), written in the same transaction as the
  // bid void. The store reads them off the loaded bundle and keeps no parallel
  // copy: another admin's withdrawal has to be visible here after a reload,
  // and a local mirror could only disagree with the server about it.

  const bundleById = (bundleId: number): Bundle | undefined =>
    bundles.value.find(b => b.id === bundleId);

  /**
   * Adopt the server's settled bundle. Every lifecycle response carries the
   * recalculated starting bid + FMV, the pause flag and the per-member
   * withdrawal marks — apply them wholesale instead of re-deriving the
   * consequences of the action locally.
   */
  function applyBundle(bundle: Bundle): void {
    const idx = bundles.value.findIndex(b => b.id === bundle.id);
    if (idx >= 0) bundles.value.splice(idx, 1, bundle);
    else bundles.value = [bundle, ...bundles.value];
  }

  function isBundlePaused(bundleId: number): boolean {
    return bundleById(bundleId)?.isPaused === true;
  }

  function isMemberWithdrawn(bundleId: number, itemId: number): boolean {
    const component = bundleById(bundleId)?.components.find(c => c.auctionItemId === itemId);
    return component?.withdrawnAt != null;
  }

  /** Members not withdrawn — what bidders can still see. */
  function visibleMemberCount(bundleId: number): number {
    const b = bundleById(bundleId);
    if (!b) return 0;
    return b.components.filter(c => c.withdrawnAt == null).length;
  }

  // Fresh object per call — callers hold on to the result, so a shared
  // instance would let one of them corrupt the next.
  const noMemberChange = (): BundleMemberChange => ({ ok: false, paused: false, voidedBidCount: 0 });

  // Refusals a composition change can come back with. The wire messages are
  // terse server copy; these are the sentences an admin reads.
  function applyBundleLifecycleError(e: unknown, fallback: string): void {
    if (!(e instanceof ApiError)) {
      submitError.value = fallback;
      return;
    }
    if (/already withdrawn/.test(e.message)) {
      submitError.value = 'That item is already withdrawn from the bundle.';
    } else if (/not withdrawn/.test(e.message)) {
      submitError.value = 'That item is not withdrawn, so there is nothing to restore.';
    } else {
      submitError.value = e.message;
    }
  }

  /**
   * Withdraw or restore one member of a live bundle. Uniform rule (2026-08-04
   * matrix, enforced server-side): ANY composition change while active bids
   * exist voids the whole stack, notifies every affected bidder, pauses the
   * bundle and re-sums its totals over the visible members. Withdrawal HIDES a
   * member — the component row stays, so the stored member list remains the
   * record of what the bundle was, and a restore can undo it.
   */
  async function changeBundleComposition(
    action: 'withdraw' | 'restore' | 'remove' | 'add',
    bundleId: number,
    itemId: number
  ): Promise<BundleMemberChange> {
    const uuid = auctionUuid.value;
    if (!uuid) return noMemberChange();
    submitError.value = null;
    const member = { bundleId, itemId };
    const idempotencyKey = newIdempotencyKey();
    try {
      const apiCall = {
        withdraw: adminCatalogApi.withdrawBundleMember,
        restore: adminCatalogApi.restoreBundleMember,
        remove: adminCatalogApi.removeBundleMember,
        add: adminCatalogApi.addBundleMember,
      }[action];
      const result = await apiCall(uuid, member, idempotencyKey);
      // The action landed either way; only skip adopting it into state the
      // admin has already navigated away from.
      if (auctionUuid.value === uuid) {
        const previousMembers = bundleById(bundleId)?.components ?? [];
        const members = new Set(result.bundle.components.map(component => component.auctionItemId));
        applyBundle(result.bundle);
        if (previousMembers.some(component => !members.has(component.auctionItemId))) await loadItems(uuid);
      }
      // `result.bundle.isPaused` is the actual persisted state — voidedBidCount
      // > 0 undercounts it: a composition change that drops visible membership
      // below the minimum pauses the bundle even when there was nothing to
      // void (bundleLifecycle.ts's other pause trigger).
      return { ok: true, paused: result.bundle.isPaused === true, voidedBidCount: result.voidedBidCount };
    } catch (e) {
      const fallback = {
        withdraw: "We couldn't withdraw the item.",
        restore: "We couldn't restore the item.",
        remove: "We couldn't remove the item.",
        add: "We couldn't add the item.",
      }[action];
      applyBundleLifecycleError(e, fallback);
      return noMemberChange();
    }
  }

  async function withdrawBundleMember(bundleId: number, itemId: number): Promise<BundleMemberChange> {
    return changeBundleComposition('withdraw', bundleId, itemId);
  }

  async function restoreBundleMember(bundleId: number, itemId: number): Promise<BundleMemberChange> {
    return changeBundleComposition('restore', bundleId, itemId);
  }

  /** Detach a member entirely — returns to the open catalog as standalone. */
  async function removeBundleMember(bundleId: number, itemId: number): Promise<BundleMemberChange> {
    return changeBundleComposition('remove', bundleId, itemId);
  }

  /** Add a member to a live bundle — voids the item's own bid + the bundle's, if either has one. */
  async function addBundleMember(bundleId: number, itemId: number): Promise<BundleMemberChange> {
    return changeBundleComposition('add', bundleId, itemId);
  }

  /**
   * Dissolve a bundle whose visible membership fell below two (2026-08-04
   * matrix, cases 3/4/5). ONE call: the bundle DELETE voids any active stack,
   * notifies the affected bidders, and lands previously-withdrawn members as
   * standalone withdrawn items — so nothing here may re-withdraw them, or the
   * survivors would be hidden too. Survivors list as standalone open lots at
   * their own starting bids.
   */
  async function dissolveBundle(bundleId: number): Promise<boolean> {
    return unbundle(bundleId);
  }

  /**
   * Reopen a paused bundle (accept bids again). The two-visible-member minimum
   * is the server's gate — a one-item bundle is not a bundle — and comes back
   * as 'below-minimum' for the caller to turn into the restore-or-dissolve
   * prompt. Reopening an unpaused bundle is a no-op, never an error.
   */
  async function reopenBundle(bundleId: number): Promise<{ ok: boolean; reason?: 'below-minimum' }> {
    const uuid = auctionUuid.value;
    if (!uuid) return { ok: false };
    submitError.value = null;
    try {
      const result = await adminCatalogApi.reopenBundle(uuid, bundleId);
      if (auctionUuid.value === uuid) applyBundle(result.bundle);
      return { ok: true };
    } catch (e) {
      // Only the below-minimum refusal carries a numeric visibleMemberCount
      // (bundleLifecycle.ts's reopenBundle) — any other VALIDATION_FAILED is
      // an unrelated failure and must not be mistaken for it.
      if (
        e instanceof ApiError &&
        e.code === 'VALIDATION_FAILED' &&
        typeof e.body.visibleMemberCount === 'number'
      ) {
        return { ok: false, reason: 'below-minimum' };
      }
      submitError.value = e instanceof ApiError ? e.message : "We couldn't reopen the bundle.";
      return { ok: false };
    }
  }

  // Hard-delete an item. The server allows it only while the auction is NOT
  // live and the item carries no bids — a contested lot is withdrawn, never
  // deleted, so its bid history survives (VALIDATION_FAILED otherwise).
  async function deleteItem(itemId: number): Promise<boolean> {
    const uuid = auctionUuid.value;
    if (!uuid) return false;
    const wasMember = bundledItemIds.value.has(itemId);
    try {
      await adminCatalogApi.deleteItem(uuid, itemId);
      if (auctionUuid.value !== uuid) return true;
      const idx = items.value.findIndex(i => i.id === itemId);
      if (idx >= 0) {
        items.value.splice(idx, 1);
      }
      selectedItemIds.value.delete(itemId);
      if (wasMember) await reloadCatalog();
      return true;
    } catch {
      return false;
    }
  }

  async function fetchAiDescription(itemId: number): Promise<string | null> {
    if (!auctionUuid.value) return null;
    aiState.value = 'loading';
    aiError.value = null;
    try {
      const result = await adminCatalogApi.aiDescription(auctionUuid.value, itemId);
      aiState.value = 'success';
      return result.descriptionHtml;
    } catch (e) {
      aiState.value = 'error';
      if (e instanceof ApiError && e.code === 'AI_DISABLED') {
        aiError.value = 'AI assist is unavailable for this organization.';
      } else {
        aiError.value = "We couldn't generate a description. Try again.";
      }
      return null;
    }
  }

  function toggleSelection(itemId: number, selected: boolean): void {
    const next = new Set(selectedItemIds.value);
    if (selected) next.add(itemId);
    else next.delete(itemId);
    selectedItemIds.value = next;
  }

  function toggleBundleSelection(bundleId: number, selected: boolean): void {
    const next = new Set(selectedBundleIds.value);
    if (selected) next.add(bundleId);
    else next.delete(bundleId);
    selectedBundleIds.value = next;
  }

  function selectAll(): void {
    selectedItemIds.value = new Set(items.value.map(i => i.id));
  }

  function clearSelection(): void {
    selectedItemIds.value = new Set();
    selectedBundleIds.value = new Set();
  }

  async function bulkImport(rows: ItemImportRow[]): Promise<void> {
    const uuid = auctionUuid.value;
    if (!uuid) return;
    importState.value = 'loading';
    importResult.value = null;
    importError.value = null;
    try {
      importResult.value = await adminCatalogApi.bulkImport(uuid, rows, newIdempotencyKey());
      importState.value = 'success';
      // Refresh items and categories so newly-created rows and auto-created categories appear.
      // Guard against the user switching auctions while the import was in-flight.
      if (auctionUuid.value === uuid) {
        await Promise.all([loadItems(uuid), loadCategories(uuid)]);
      }
    } catch (e) {
      importState.value = 'error';
      importError.value = describeBulkImportFailure(e);
    }
  }

  const categories = ref<Category[]>([]);
  const categoriesState = ref<LoadState>('idle');

  async function loadCategories(uuid: string): Promise<void> {
    const requestId = ++loadCategoriesRequestId;
    categoriesState.value = 'loading';
    try {
      const result = await adminCatalogApi.listCategories(uuid);
      if (requestId !== loadCategoriesRequestId) return;
      categories.value = result;
      categoriesState.value = 'success';
    } catch {
      if (requestId !== loadCategoriesRequestId) return;
      categoriesState.value = 'error';
    }
  }

  function resetCategories(): void {
    categories.value = [];
    categoriesState.value = 'idle';
  }

  async function createCategory(name: string): Promise<Category | null> {
    if (!auctionUuid.value) return null;
    try {
      const cat = await adminCatalogApi.createCategory(auctionUuid.value, name);
      categories.value = [...categories.value, cat];
      return cat;
    } catch {
      return null;
    }
  }

  async function publishItem(itemId: number): Promise<AuctionItem | null> {
    if (!auctionUuid.value) return null;
    try {
      const result = await adminCatalogApi.publishItem(auctionUuid.value, itemId);
      const idx = items.value.findIndex(i => i.id === result.id);
      if (idx >= 0) items.value.splice(idx, 1, result);
      return result;
    } catch {
      return null;
    }
  }

  async function duplicateItem(itemId: number): Promise<AuctionItem | null> {
    if (!auctionUuid.value) return null;
    try {
      const result = await adminCatalogApi.duplicateItem(auctionUuid.value, itemId);
      items.value = [result, ...items.value];
      return result;
    } catch {
      return null;
    }
  }

  async function bulkPublish(itemIds: number[]): Promise<BulkActionResult[]> {
    if (!auctionUuid.value) return [];
    try {
      const results = await adminCatalogApi.bulkPublish(auctionUuid.value, itemIds);
      await loadItems(auctionUuid.value);
      clearSelection();
      return results;
    } catch {
      return [];
    }
  }

  async function bulkWithdraw(itemIds: number[], reason?: string): Promise<BulkActionResult[]> {
    if (!auctionUuid.value) return [];
    try {
      const results = await adminCatalogApi.bulkWithdraw(auctionUuid.value, itemIds, reason);
      await loadItems(auctionUuid.value);
      clearSelection();
      return results;
    } catch {
      return [];
    }
  }

  /**
   * Re-stamp the auction's current pricing defaults across a selection.
   *
   * Unlike its best-effort siblings above, the server applies this in one
   * transaction. A failure therefore means nothing was written, and the caller
   * has to be told: swallowing it to an empty result — what bulkPublish and
   * friends do — would read as "0 rows needed changing" and leave the admin
   * believing the run succeeded.
   */
  async function reapplyDefaults(
    itemIds: number[],
    bundleIds: number[]
  ): Promise<ReapplyDefaultsResponse> {
    const uuid = auctionUuid.value;
    if (!uuid) throw new Error('No auction is loaded.');
    submitError.value = null;
    fieldErrors.value = {};
    try {
      const result = await adminCatalogApi.reapplyDefaults(uuid, itemIds, bundleIds);
      // The admin can switch auctions while this request is in flight, and
      // again while the refresh below is. `loadItems` reassigns
      // `auctionUuid`, so refreshing against the captured uuid would drag the
      // store back onto the auction they just left. Both awaits need their
      // own check: clearing a selection the admin has since built on a
      // different auction is the same bug one suspension point later.
      if (auctionUuid.value === uuid) {
        await Promise.all([loadItems(uuid), loadBundles(uuid)]);
        if (auctionUuid.value === uuid) clearSelection();
      }
      return result;
    } catch (e) {
      submitError.value =
        e instanceof ApiError ? e.message : "We couldn't re-apply the pricing defaults.";
      throw e;
    }
  }

  async function bulkDelete(itemIds: number[]): Promise<BulkActionResult[]> {
    if (!auctionUuid.value) return [];
    try {
      const results = await adminCatalogApi.bulkDelete(auctionUuid.value, itemIds);
      await loadItems(auctionUuid.value);
      clearSelection();
      return results;
    } catch {
      return [];
    }
  }

  async function resetForAuction(uuid: string): Promise<void> {
    if (uuid === auctionUuid.value) {
      // Same auction context still active (e.g. the admin navigated away and
      // back without picking a different auction) — search/pagination/
      // selection state is worth preserving, but the underlying item/bundle
      // data itself can have gone stale in the meantime (GT1-446 #4: a bid
      // landing on a bundle while the admin was on another page). Refresh the
      // data in place instead of no-op'ing entirely. Categories must reload
      // too: Configure can set auctionUuid (via loadItems) without ever
      // populating this store's categories, so arriving here with a
      // matching uuid is not proof categories were ever loaded.
      await Promise.all([loadItems(uuid), loadBundles(uuid), loadCategories(uuid)]);
      return;
    }

    auctionUuid.value = uuid;
    submitRequestId++;
    orderSaveRequestId++;
    orderSaveState.value = LoadState.idle;

    items.value = [];
    bundles.value = [];
    categories.value = [];
    bidCounts.value = {};
    selectedItemIds.value = new Set();
    selectedBundleIds.value = new Set();

    page.value = 0;
    searchQuery.value = '';
    categoryFilter.value = [];

    itemsState.value = 'idle';
    bundlesState.value = 'idle';
    categoriesState.value = 'idle';
    bidCountsState.value = 'idle';
    importState.value = 'idle';
    importResult.value = null;
    importError.value = null;
    submitState.value = 'idle';
    submitError.value = null;
    fieldErrors.value = {};

    await Promise.all([
      loadItems(uuid),
      loadBundles(uuid),
      loadBidCounts(uuid),
      loadCategories(uuid),
    ]);
  }

  async function publishBundle(bundleId: number): Promise<Bundle | null> {
    const uuid = auctionUuid.value;
    if (!uuid) return null;
    submitError.value = null;
    try {
      const result = await adminCatalogApi.publishBundle(uuid, bundleId);
      if (auctionUuid.value !== uuid) return result;
      const idx = bundles.value.findIndex(b => b.id === result.id);
      if (idx >= 0) bundles.value.splice(idx, 1, result);
      // Publishing a bundle cascades to its component items server-side —
      // refresh items so the sidebar's draft-item count and catalog rows
      // reflect the components' new status.
      await loadItems(uuid);
      return result;
    } catch (e) {
      submitError.value = e instanceof ApiError ? e.message : "We couldn't publish the bundle.";
      return null;
    }
  }

  // Publish every draft unit — bundles first (each cascades to its member
  // items server-side), then standalone draft items in one bulk call.
  // Returns counts in the same bidder-visible units as draftContentCount.
  async function publishAllDraftContent(): Promise<{ published: number; failed: number }> {
    if (!auctionUuid.value) return { published: 0, failed: 0 };
    let published = 0;
    let failed = 0;
    for (const bundleId of [...draftBundleIds.value]) {
      const result = await publishBundle(bundleId);
      if (result) published++;
      else failed++;
    }
    const itemIds = [...draftStandaloneItemIds.value];
    if (itemIds.length > 0) {
      const results = await bulkPublish(itemIds);
      if (results.length === 0) {
        failed += itemIds.length;
      } else {
        published += results.filter(r => r.success).length;
        failed += results.filter(r => !r.success).length;
      }
    }
    return { published, failed };
  }

  async function unpublishBundle(bundleId: number): Promise<Bundle | null> {
    if (!auctionUuid.value) return null;
    submitError.value = null;
    try {
      const result = await adminCatalogApi.unpublishBundle(auctionUuid.value, bundleId);
      const idx = bundles.value.findIndex(b => b.id === result.id);
      if (idx >= 0) bundles.value.splice(idx, 1, result);
      return result;
    } catch (e) {
      submitError.value = e instanceof ApiError ? e.message : "We couldn't unpublish the bundle.";
      return null;
    }
  }

  // Freeing the items is server work: the DELETE voids any active stack,
  // notifies its bidders, and keeps withdrawn members hidden by landing them as
  // standalone withdrawn items. Items are reloaded because their status and
  // bundle membership both changed underneath the catalog.
  async function unbundle(bundleId: number): Promise<boolean> {
    if (!auctionUuid.value) return false;
    submitError.value = null;
    try {
      await adminCatalogApi.deleteBundle(auctionUuid.value, bundleId);
      const idx = bundles.value.findIndex(b => b.id === bundleId);
      if (idx >= 0) bundles.value.splice(idx, 1);
      await loadItems(auctionUuid.value);
      return true;
    } catch (e) {
      if (e instanceof ApiError && /appears on an invoice/.test(e.message)) {
        submitError.value = 'This bundle is already on an invoice, so it can no longer be dissolved.';
      } else {
        submitError.value = e instanceof ApiError ? e.message : "We couldn't dissolve the bundle.";
      }
      return false;
    }
  }

  /**
   * Withdraw the WHOLE bundle from the catalog: a shortcut that keeps
   * composition intact (unlike unbundle's dissolve, `bundle_components` rows
   * survive) but hides the bundle from bidders, same as any other withdrawn
   * catalog entry. Any active bid on the bundle voids, same consequence as
   * unbundle. Member items' own status is left untouched — they were never
   * independently visible while bundled — so items don't need reloading.
   */
  async function withdrawBundle(bundleId: number): Promise<boolean> {
    if (!auctionUuid.value) return false;
    submitError.value = null;
    try {
      const result = await adminCatalogApi.withdrawBundle(auctionUuid.value, bundleId);
      applyBundle(result.bundle);
      return true;
    } catch (e) {
      submitError.value = e instanceof ApiError ? e.message : "We couldn't withdraw the bundle.";
      return false;
    }
  }

  // Bundle saves can fail because a member id went stale underneath the
  // open drawer (another admin deleted or withdrew the item). The generic
  // fallback explains nothing, so name what happened and refresh the
  // catalog: the picker recomputes from items, dropping the dead member.
  async function applyBundleApiError(e: ApiError, uuid: string): Promise<void> {
    const staleMember =
      e.code === 'VALIDATION_FAILED' &&
      /component item (not in scope|is withdrawn)/.test(e.message);
    if (staleMember) {
      submitState.value = 'error';
      submitError.value =
        'An item in this bundle is no longer available. We refreshed the list, review the items and try again.';
      await Promise.all([loadItems(uuid), loadBundles(uuid)]);
      return;
    }
    applyApiError(e);
  }

  async function saveBundle(input: BundleCreateInput): Promise<Bundle | null> {
    const uuid = auctionUuid.value;
    if (!uuid) return null;
    const requestId = ++submitRequestId;
    submitState.value = 'loading';
    submitError.value = null;
    fieldErrors.value = {};
    try {
      const result = await adminCatalogApi.createBundle(uuid, input, newIdempotencyKey());
      if (requestId !== submitRequestId || auctionUuid.value !== uuid) {
        if (auctionUuid.value !== uuid) submitState.value = 'idle';
        return result;
      }
      bundles.value = [result, ...bundles.value];
      await loadItems(uuid);
      submitState.value = 'success';
      return result;
    } catch (e) {
      if (requestId !== submitRequestId) return null;
      if (e instanceof ApiError) await applyBundleApiError(e, uuid);
      else submitError.value = "We couldn't save the bundle.";
      return null;
    }
  }

  async function updateBundle(bundleId: number, input: BundleUpdateInput): Promise<Bundle | null> {
    const uuid = auctionUuid.value;
    if (!uuid) return null;
    const requestId = ++submitRequestId;
    submitState.value = 'loading';
    submitError.value = null;
    fieldErrors.value = {};
    try {
      const result = await adminCatalogApi.updateBundle(uuid, bundleId, input);
      if (requestId !== submitRequestId || auctionUuid.value !== uuid) {
        if (auctionUuid.value !== uuid) submitState.value = 'idle';
        return result;
      }
      const idx = bundles.value.findIndex(b => b.id === result.id);
      if (idx >= 0) bundles.value.splice(idx, 1, result);
      await loadItems(uuid);
      submitState.value = 'success';
      return result;
    } catch (e) {
      if (requestId !== submitRequestId) return null;
      if (e instanceof ApiError) await applyBundleApiError(e, uuid);
      else submitError.value = "We couldn't save the bundle.";
      return null;
    }
  }

  return {
    auctionUuid,
    items,
    itemsState,
    itemsError,
    selectedItemIds,
    selectedBundleIds,
    selectionCount,
    bundles,
    bundlesState,
    submitState,
    submitError,
    fieldErrors,
    aiState,
    aiError,
    importState,
    importResult,
    importError,
    page,
    pageSize,
    totalCount,
    searchQuery,
    categoryFilter,
    hasActiveFilters,
    categories,
    categoriesState,
    bidCounts,
    bidCountsState,
    loadItems,
    loadBidCounts,
    bidCountFor,
    loadBundles,
    setPage,
    setPageSize,
    setSearch,
    setCategoryFilter,
    loadCategories,
    resetCategories,
    createCategory,
    saveItem,
    publishItem,
    duplicateItem,
    withdrawItems,
    isBundlePaused,
    isMemberWithdrawn,
    visibleMemberCount,
    withdrawBundleMember,
    restoreBundleMember,
    removeBundleMember,
    addBundleMember,
    reopenBundle,
    dissolveBundle,
    deleteItem,
    bulkPublish,
    bulkWithdraw,
    bulkDelete,
    fetchAiDescription,
    toggleSelection,
    toggleBundleSelection,
    selectAll,
    clearSelection,
    reapplyDefaults,
    bulkImport,
    saveBundle,
    updateBundle,
    publishBundle,
    unpublishBundle,
    unbundle,
    withdrawBundle,
    resetForAuction,
    bundledItemIds,
    draftStandaloneItemIds,
    draftBundleIds,
    draftContentCount,
    catalogEntries,
    catalogUnitCount,
    orderSaveState,
    saveCatalogOrder,
    reloadCatalog,
    publishAllDraftContent,
  };
});
